import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const StatusBadge = ({ status }) => {
  const map = {
    active: 'bg-success/10 text-success',
    suspended: 'bg-error/10 text-error',
    pending: 'bg-warning/10 text-warning',
    draft: 'bg-muted text-muted-foreground',
    completed: 'bg-primary/10 text-primary',
  };
  return (
    <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${map?.[status] || 'bg-muted text-muted-foreground'}`}>
      {status}
    </span>
  );
};

const BrandDetailModal = ({ brand, onClose, onToggleStatus }) => (
  <div className="fixed inset-0 bg-black/70 z-[200] flex items-center justify-center p-4">
    <div className="bg-card rounded-2xl border border-border w-full max-w-lg max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between p-5 border-b border-border">
        <h3 className="text-base font-semibold text-foreground">Brand Profile</h3>
        <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground">
          <Icon name="X" size={16} />
        </button>
      </div>
      <div className="p-5 space-y-5">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-secondary/10 flex items-center justify-center">
            <Icon name="Building2" size={24} color="var(--color-secondary)" />
          </div>
          <div>
            <p className="text-base font-semibold text-foreground">{brand?.name}</p>
            <p className="text-sm text-muted-foreground">{brand?.email}</p>
            <StatusBadge status={brand?.status} />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Campaigns', value: brand?.campaignCount, icon: 'Megaphone', color: 'text-primary' },
            { label: 'Budget Used', value: `$${parseFloat(brand?.budgetUsed || 0)?.toFixed(0)}`, icon: 'DollarSign', color: 'text-success' },
            { label: 'Total Spent', value: `$${parseFloat(brand?.totalSpent || 0)?.toFixed(0)}`, icon: 'TrendingUp', color: 'text-secondary' },
          ]?.map((s, i) => (
            <div key={i} className="bg-muted/50 rounded-lg p-3 text-center">
              <Icon name={s?.icon} size={16} className={`${s?.color} mx-auto mb-1`} />
              <p className="text-lg font-bold text-foreground">{s?.value}</p>
              <p className="text-xs text-muted-foreground">{s?.label}</p>
            </div>
          ))}
        </div>
        {brand?.campaigns?.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Active Campaigns</p>
            <div className="space-y-2">
              {brand?.campaigns?.map((c, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-muted/40 rounded-lg">
                  <p className="text-sm text-foreground">{c?.title}</p>
                  <StatusBadge status={c?.status} />
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="flex gap-2 pt-2">
          <Button
            variant={brand?.status === 'active' ? 'outline' : 'default'}
            size="sm"
            iconName={brand?.status === 'active' ? 'UserX' : 'UserCheck'}
            onClick={() => { onToggleStatus(brand?.id, brand?.is_active); onClose(); }}
            className="flex-1"
          >
            {brand?.status === 'active' ? 'Suspend Brand' : 'Activate Brand'}
          </Button>
        </div>
      </div>
    </div>
  </div>
);

const BrandManagement = ({ currentAdminId }) => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedBrand, setSelectedBrand] = useState(null);

  const loadBrands = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('user_profiles')
        ?.select('id, full_name, email, is_active, created_at')
        ?.eq('role', 'brand')
        ?.order('created_at', { ascending: false });
      if (!error && data) {
        const enriched = await Promise.all(
          data?.map(async (b) => {
            const { data: campaigns } = await supabase?.from('campaigns')?.select('id, title, status, budget, spent')?.eq('brand_id', b?.id);
            const campaignCount = campaigns?.length || 0;
            const budgetUsed = campaigns?.reduce((s, c) => s + parseFloat(c?.spent || 0), 0) || 0;
            const totalSpent = campaigns?.reduce((s, c) => s + parseFloat(c?.budget || 0), 0) || 0;
            return {
              id: b?.id,
              name: b?.full_name || b?.email?.split('@')?.[0] || 'Brand',
              email: b?.email || '',
              status: b?.is_active ? 'active' : 'suspended',
              is_active: b?.is_active,
              campaignCount,
              budgetUsed,
              totalSpent,
              joinDate: new Date(b?.created_at)?.toLocaleDateString(),
              campaigns: (campaigns || [])?.slice(0, 5),
            };
          })
        );
        setBrands(enriched);
      }
    } catch (err) {
      console.error('[BrandManagement] loadBrands error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadBrands(); }, [loadBrands]);

  const handleToggleStatus = async (brandId, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      const { error } = await supabase?.from('user_profiles')?.update({ is_active: newStatus })?.eq('id', brandId);
      if (!error) {
        setBrands(prev => prev?.map(b => b?.id === brandId ? { ...b, is_active: newStatus, status: newStatus ? 'active' : 'suspended' } : b));
        await supabase?.from('moderation_logs')?.insert({
          moderator_id: currentAdminId,
          target_user_id: brandId,
          action: 'reviewed',
          notes: newStatus ? 'Brand activated by admin' : 'Brand suspended by admin',
        });
      }
    } catch (err) {
      console.error('[BrandManagement] handleToggleStatus error:', err);
    }
  };

  const filtered = brands?.filter(b => {
    const matchSearch = b?.name?.toLowerCase()?.includes(search?.toLowerCase()) || b?.email?.toLowerCase()?.includes(search?.toLowerCase());
    const matchStatus = statusFilter === 'all' || b?.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div className="space-y-5">
      <div className="bg-card rounded-xl border border-border p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
          <div>
            <h3 className="text-base font-semibold text-foreground">Brand Partners</h3>
            <p className="text-sm text-muted-foreground">{filtered?.length} brands</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-48"><Input type="search" placeholder="Search brands..." value={search} onChange={(e) => setSearch(e?.target?.value)} /></div>
            <div className="w-36">
              <Select
                options={[{ value: 'all', label: 'All Status' }, { value: 'active', label: 'Active' }, { value: 'suspended', label: 'Suspended' }]}
                value={statusFilter} onChange={setStatusFilter}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  {['Brand', 'Campaigns', 'Budget Used', 'Total Budget', 'Status', 'Joined', 'Actions']?.map(h => (
                    <th key={h} className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered?.map((b) => (
                  <tr key={b?.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
                          <Icon name="Building2" size={14} color="var(--color-secondary)" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{b?.name}</p>
                          <p className="text-xs text-muted-foreground">{b?.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-3"><span className="text-sm font-semibold text-primary">{b?.campaignCount}</span></td>
                    <td className="py-3 px-3"><span className="text-sm text-success">${parseFloat(b?.budgetUsed || 0)?.toFixed(0)}</span></td>
                    <td className="py-3 px-3"><span className="text-sm text-foreground">${parseFloat(b?.totalSpent || 0)?.toFixed(0)}</span></td>
                    <td className="py-3 px-3"><StatusBadge status={b?.status} /></td>
                    <td className="py-3 px-3"><span className="text-xs text-muted-foreground">{b?.joinDate}</span></td>
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setSelectedBrand(b)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title="View">
                          <Icon name="Eye" size={14} />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(b?.id, b?.is_active)}
                          className={`p-1.5 rounded-lg transition-colors ${b?.is_active ? 'hover:bg-error/10 text-muted-foreground hover:text-error' : 'hover:bg-success/10 text-muted-foreground hover:text-success'}`}
                          title={b?.is_active ? 'Suspend' : 'Activate'}
                        >
                          <Icon name={b?.is_active ? 'UserX' : 'UserCheck'} size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered?.length === 0 && (
                  <tr><td colSpan={7} className="py-10 text-center text-sm text-muted-foreground">No brands found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {selectedBrand && (
        <BrandDetailModal
          brand={selectedBrand}
          onClose={() => setSelectedBrand(null)}
          onToggleStatus={handleToggleStatus}
        />
      )}
    </div>
  );
};

export default BrandManagement;
